import { useCallback, useEffect, useMemo, useState } from 'react'

import { SalonDataTable } from '@/components/ui/SalonDataTable'
import { fetchPromotionalAudience } from '@/lib/api/messagingSettings'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { formatE164Display } from '@/lib/phone'
import { cn } from '@/lib/utils'

/** @typedef {import('@/lib/api/messagingSettings').PromotionalAudienceCustomer} PromoCustomer */

const FILTER_PRESETS = [
  { id: '', label: 'Everyone' },
  { id: 'marketing_consent', label: 'Agreed to offers' },
  { id: 'inactive_60', label: 'Not visited 60+ days' },
  { id: 'regular', label: 'Regular (3+ visits)' },
  { id: 'big_spenders', label: 'Big spenders' },
  { id: 'never_visited', label: 'Never visited' },
]

const inputClass = cn(
  'h-10 w-full rounded-xl border border-border/80 bg-card px-3 text-sm',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

function parseAmount(value) {
  const trimmed = String(value ?? '').trim()
  if (trimmed === '') return null
  const amount = Number(trimmed)
  return Number.isFinite(amount) && amount >= 0 ? amount : null
}

function memberLabel(customer) {
  if (customer.member_days >= 365) {
    const years = Math.floor(customer.member_days / 365)
    return `${years} yr${years === 1 ? '' : 's'}`
  }

  if (customer.member_days >= 30) {
    const months = Math.floor(customer.member_days / 30)
    return `${months} mo`
  }

  return customer.customer_since_label ?? '—'
}

function lastVisitRelative(customer) {
  if (customer.days_since_visit === null) return null
  if (customer.days_since_visit === 0) return 'Today'
  if (customer.days_since_visit === 1) return 'Yesterday'
  return `${customer.days_since_visit} days ago`
}

function lastVisitLabel(customer) {
  if (customer.days_since_visit === null) {
    return <span>Never</span>
  }

  const relative = lastVisitRelative(customer)

  return (
    <>
      <p className="text-foreground">{customer.last_visit_label ?? '—'}</p>
      {relative ? <p className="text-xs text-muted-foreground">{relative}</p> : null}
    </>
  )
}

/**
 * @param {string} preset
 * @param {string} spendMin
 * @param {string} spendMax
 */
function buildFilterParams(preset, spendMin, spendMax) {
  /** @type {Record<string, unknown>} */
  const params = {}

  switch (preset) {
    case 'marketing_consent':
      params.marketing_consent = true
      break
    case 'inactive_60':
      params.inactive_days = 60
      break
    case 'regular':
      params.min_visits = 3
      break
    case 'never_visited':
      params.max_visits = 0
      break
    default:
      break
  }

  const minSpend = parseAmount(spendMin)
  const maxSpend = parseAmount(spendMax)

  if (minSpend !== null) params.min_spend = minSpend
  if (maxSpend !== null) params.max_spend = maxSpend

  if (preset === 'big_spenders' && minSpend === null && maxSpend === null) {
    params.min_spend = 500
  }

  return params
}

/** @type {import('@/components/ui/SalonDataTable').SalonDataTableColumn[]} */
const TABLE_COLUMNS = [
  {
    key: 'name',
    label: 'Customer',
    sortable: true,
    render: (customer) => (
      <>
        <p className="font-medium text-foreground">{customer.name}</p>
        <p className="text-xs text-muted-foreground">
          {customer.phone ? formatE164Display(customer.phone) : 'No phone'}
          {customer.marketing_consent ? ' · Offers OK' : ''}
        </p>
      </>
    ),
  },
  {
    key: 'created_at',
    label: 'With us',
    sortable: true,
    render: (customer) => memberLabel(customer),
    className: 'text-muted-foreground',
  },
  {
    key: 'visit_count',
    label: 'Visits',
    sortable: true,
    className: 'text-right tabular-nums',
    render: (customer) => customer.visit_count,
  },
  {
    key: 'lifetime_spend',
    label: 'Spent',
    sortable: true,
    className: 'text-right tabular-nums',
    render: (customer) => (
      <>AED {Number(customer.lifetime_spend).toLocaleString(undefined, { maximumFractionDigits: 0 })}</>
    ),
  },
  {
    key: 'last_visit_at',
    label: 'Last visit',
    sortable: true,
    render: (customer) => lastVisitLabel(customer),
  },
]

/**
 * @param {{
 *   selectedIds: Set<number>,
 *   onSelectionChange: (ids: Set<number>) => void,
 * }} props
 */
export function PromotionalCustomerPicker({ selectedIds, onSelectionChange }) {
  const [items, setItems] = useState(/** @type {PromoCustomer[]} */ ([]))
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, per_page: 25, total: 0 })
  const [page, setPage] = useState(1)
  const [preset, setPreset] = useState('')
  const [spendMin, setSpendMin] = useState('')
  const [spendMax, setSpendMax] = useState('')
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [sortBy, setSortBy] = useState('lifetime_spend')
  const [sortDir, setSortDir] = useState('desc')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQ(q.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [q])

  useEffect(() => {
    setPage(1)
  }, [preset, spendMin, spendMax, debouncedQ, sortBy, sortDir])

  const filterParams = useMemo(
    () => buildFilterParams(preset, spendMin, spendMax),
    [preset, spendMin, spendMax],
  )

  const loadAudience = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const result = await fetchPromotionalAudience({
        page,
        per_page: 25,
        q: debouncedQ || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
        only_with_phone: true,
        ...filterParams,
      })
      setItems(result.items)
      setMeta(result.meta)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load customers.'))
    } finally {
      setLoading(false)
    }
  }, [page, debouncedQ, sortBy, sortDir, filterParams])

  useEffect(() => {
    loadAudience()
  }, [loadAudience])

  const pageIds = useMemo(() => items.map((row) => row.id), [items])
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id))

  function toggleRow(id) {
    const next = new Set(selectedIds)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    onSelectionChange(next)
  }

  function togglePage() {
    const next = new Set(selectedIds)
    if (allOnPageSelected) {
      pageIds.forEach((id) => next.delete(id))
    } else {
      pageIds.forEach((id) => next.add(id))
    }
    onSelectionChange(next)
  }

  function handlePresetClick(id) {
    setPreset(id)
    if (id === 'big_spenders') {
      setSpendMin('500')
      setSpendMax('')
    } else if (id === '') {
      setSpendMin('')
      setSpendMax('')
    }
  }

  function handleSort(columnKey) {
    if (sortBy === columnKey) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortBy(columnKey)
    setSortDir(columnKey === 'name' ? 'asc' : 'desc')
  }

  return (
    <SalonDataTable
      className="border-0 bg-transparent shadow-none"
      searchPlaceholder="Search name or phone…"
      searchValue={q}
      onSearchChange={setQ}
      columns={TABLE_COLUMNS}
      rows={items}
      loading={loading}
      error={error}
      getRowKey={(row) => row.id}
      sortBy={sortBy}
      sortDir={sortDir}
      onSortChange={handleSort}
      meta={meta}
      onPageChange={setPage}
      emptyLabel="No customers match this filter."
      selection={{
        selectedIds,
        getRowId: (row) => row.id,
        isRowSelected: (row) => selectedIds.has(row.id),
        onToggleRow: toggleRow,
        onTogglePage: togglePage,
        allOnPageSelected,
      }}
      filters={(
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="font-medium text-primary">
              {selectedIds.size} selected
            </span>
            {selectedIds.size > 0 ? (
              <button
                type="button"
                onClick={() => onSelectionChange(new Set())}
                className="text-muted-foreground underline-offset-2 hover:underline"
              >
                Clear selection
              </button>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2">
            {FILTER_PRESETS.map((filter) => (
              <button
                key={filter.id || 'all'}
                type="button"
                onClick={() => handlePresetClick(filter.id)}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                  preset === filter.id
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border/70 bg-card text-foreground hover:bg-muted/50',
                )}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <details className="rounded-xl border border-border/60 bg-muted/15 px-4 py-3">
            <summary className="cursor-pointer text-sm font-medium text-foreground">
              More filters — spend between (AED)
            </summary>
            <p className="mt-2 text-xs text-muted-foreground">
              Leave &quot;To&quot; blank for no upper limit.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="mb-1 block text-muted-foreground">From</span>
                <input
                  type="number"
                  min="0"
                  step="50"
                  value={spendMin}
                  onChange={(event) => {
                    setSpendMin(event.target.value)
                    if (preset !== 'big_spenders') setPreset('')
                  }}
                  placeholder="e.g. 500"
                  className={inputClass}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block text-muted-foreground">To</span>
                <input
                  type="number"
                  min="0"
                  step="50"
                  value={spendMax}
                  onChange={(event) => {
                    setSpendMax(event.target.value)
                    if (preset !== 'big_spenders') setPreset('')
                  }}
                  placeholder="No limit"
                  className={inputClass}
                />
              </label>
            </div>
          </details>
        </div>
      )}
    />
  )
}
